import React, { Component, Fragment } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import Notification from './Notification';
import Navigation from './Navigation';
import Cart from '../Selection/Cart';
import Select from '../Elements/Select/Select';
import { translate, prefix } from '../../utils/locale';
import { addEvent, removeEvent, hasClass, addClass, removeClass, offset, scrollTo } from '../../utils/dom';
import { removeNotification, openCart, openMenu, clearFilter, resetFilter, changeLanguage, goToPage, changeDevice } from '../../actions/environment';
import { clientInit, changeCountry, changeAddressInput } from '../../actions/checkout';
import s from './Header.scss';

const headerOffset = 50;
const stickyOffset = 0;
const breakPoints = [900, 600];
const media = { w_600: 'mobile', w_900: 'tablet', w_1200: 'desktop' };

class Header extends Component {
	constructor(props) {
		super(props);

		this.state = {
			location: false,
			grid: false,
			menuPopupHeight: 'auto',
		};

		this.headerElement = React.createRef();
		this.linksElement = React.createRef();
		this.cartElement = React.createRef();
		this.languageCountryElement = React.createRef();
	}

	componentDidMount() {
		if (typeof window !== 'undefined') {
			const $html = document.querySelector('html');
			let currentWidth = breakPoints.reduce((acc, bp) => (bp > window.innerWidth ? bp : acc), 1200);
			let device = media[`w_${currentWidth}`];

			this.props.clientInit();
			this.props.changeDevice(device);

			// Wait for page to be rendered with all relevant elements.
			const initiateSiteListeners = () => {
				if (!this.headerElement || !this.linksElement || !this.cartElement) {
					setTimeout(initiateSiteListeners, 200);
				}

				const startTop = window.pageYOffset || document.documentElement.scrollTop;
				const initialOffset = offset(this.headerElement.current).top + stickyOffset;
				let menuOffset = initialOffset;
				let menuOffsetWindowTop = startTop > menuOffset ? 0 : menuOffset - startTop;

				let isScrolled = false;
				let headerIsFixed = false;

				if (this.state.menuPopupHeight === 'auto') {
					this.setState({
						menuPopupHeight: window.innerHeight - menuOffsetWindowTop - headerOffset,
					});
				}

				const resizeEvent = () => {
					const updatedWidth = breakPoints.reduce((acc, bp) => (bp > window.innerWidth ? bp : acc), 1200);
					const top = window.pageYOffset || document.documentElement.scrollTop;
					menuOffset = offset(this.headerElement.current).top + stickyOffset;
					menuOffsetWindowTop = top > menuOffset ? 0 : menuOffset - top;
					this.setState({
						menuPopupHeight: window.innerHeight - menuOffsetWindowTop - headerOffset,
					});

					// Switch between mobile and desktop.
					if (currentWidth !== updatedWidth) {
						device = media[`w_${updatedWidth}`];
						this.props.changeDevice(device);
						currentWidth = updatedWidth;
					}
				};

				addEvent(window, 'resize.logoHeader', () => {
					window.requestAnimationFrame(resizeEvent);
				});

				const scrollEvent = () => {
					const top = window.pageYOffset || document.documentElement.scrollTop;
					menuOffset = offset(this.headerElement.current).top + stickyOffset;
					menuOffsetWindowTop = top > menuOffset ? 0 : menuOffset - top;
					this.setState({
						menuPopupHeight: window.innerHeight - menuOffsetWindowTop - headerOffset,
					});

					// make logo sticky when level with header.
					if (!headerIsFixed && top >= initialOffset) {
						headerIsFixed = true;
						addClass($html, 'headerIsFixed');
					} else if (headerIsFixed && top < initialOffset) {
						headerIsFixed = false;
						removeClass($html, 'headerIsFixed');
					}

					if (top > 0 && !isScrolled) {
						addClass($html, 'is-scrolled');
						isScrolled = true;
					} else if (top === 0) {
						removeClass($html, 'is-scrolled');
						isScrolled = false;
					}
				};

				addEvent(window, 'scroll.logoHeader', () => {
					window.requestAnimationFrame(scrollEvent);
				});

				addEvent(document.querySelector('body'), 'click.cartClose', (e) => {
					if (this.cartElement.current && !this.cartElement.current.contains(e.target) && !this.headerElement.current.contains(e.target) && this.props.properties.openCart) {
						e.preventDefault();
						e.stopPropagation();
						this.props.openCart(false);
					}
					if (this.languageCountryElement.current && !this.languageCountryElement.current.contains(e.target) && this.state.location) {
						e.preventDefault();
						e.stopPropagation();
						this.setState({ location: false });
					}
				});
			};

			initiateSiteListeners();

			if ('ontouchstart' in document.documentElement) {
				removeClass($html, 'no-touchevents');
				addClass($html, 'touchevents');
			}

			if (hasClass($html, 'no-touchevents')) {
				addEvent(window, 'touchstart.touchTest', () => {
					removeClass($html, 'no-touchevents');
					addClass($html, 'touchevents');
					removeEvent(window, 'touchstart.touchTest');
				});
			}

			addEvent(document.querySelector('body'), 'click.linkCapture', (e) => {
				const $link = e.target.closest('a');

				if ($link && this.props.checkout.klarnaUnloaded && $link.getAttribute('href').indexOf('#') !== 0) {
					e.preventDefault();
					e.stopPropagation();
					window.location = $link.href;
					// Force full page reload to clear Klarna on next page click.
				} else if ($link && $link.getAttribute('href').indexOf('#') === 0 && document.getElementById($link.getAttribute('href').slice(1))) {
					e.preventDefault();
					const scrollPosition = offset(document.getElementById($link.getAttribute('href').slice(1))).top - 100;
					scrollTo(400, scrollPosition);
				} else if ($link && hasClass($link, 'linkCapture') && !/(http|mailto|tel)/.test($link.getAttribute('href'))) {
					e.preventDefault();
					this.props.goToPage($link.getAttribute('href'));
				}
			});

			addEvent(window, 'keydown.general', (e) => {
				if (e.key === 'Escape') {
					if (this.props.properties.openCart) this.props.openCart(false);
					if (this.state.location === true) this.setState({ location: false });
					if (this.state.grid) this.setState({ grid: false });
				} else if (e.shiftKey && e.ctrlKey && e.key === 'A') {
					// ctrl + shift + a = Toggle grid
					this.setState((prevState) => ({ grid: !prevState.grid }));
				}
			});
		}
	}

	componentWillUnmount() {
		removeEvent(document.querySelector('body'), 'click.cartClose');
		removeEvent(window, 'keydown.general');
		removeEvent(window, 'scroll.logoHeader');
		removeEvent(window, 'resize.logoHeader');
	}

	openLocation = (e) => {
		e.preventDefault();

		this.setState((prevState) => ({ location: !prevState.location }));
	}

	changeLanguage = (id, lang) => {
		const { pagePaths } = this.props;
		this.props.changeLanguage(lang);
		if (lang !== 'en') {
			this.props.goToPage(`/${lang}/${pagePaths && pagePaths[lang] ? pagePaths[lang] : ''}`);
		} else {
			this.props.goToPage(`/${pagePaths && pagePaths[lang] ? pagePaths[lang] : ''}`);
		}
		this.setState({
			location: false,
		});
	}

	closeCartHandler = () => {
		this.props.openCart(false);
	}

	openCartHandler = (e) => {
		if (!this.props.properties.openCart) {
			e.preventDefault();

			const top = window.pageYOffset || document.documentElement.scrollTop;
			const menuOffset = offset(this.headerElement.current).top + stickyOffset;
			const menuOffsetWindowTop = top > menuOffset ? 0 : menuOffset - top;
			this.setState({
				menuPopupHeight: window.innerHeight - menuOffsetWindowTop - headerOffset,
			});

			if (this.props.navigation.open) this.props.openMenu();
			this.props.openCart(true);
		} else if (this.props.checkout.klarnaUnloaded) {
			e.preventDefault();
			window.location = `${window.location.origin}/${this.props.setup.language}/selection`;
		}
	}

	notificationRemovedHandler = () => {
		setTimeout(() => {
			const top = window.pageYOffset || document.documentElement.scrollTop;
			const menuOffset = offset(this.headerElement.current).top + stickyOffset;
			const menuOffsetWindowTop = top > menuOffset ? 0 : menuOffset - top;
			this.setState({
				menuPopupHeight: window.innerHeight - menuOffsetWindowTop - headerOffset,
			});
		}, 500);
	}

	openMobileMenuHandler = (e) => {
		if (e) e.preventDefault();

		const top = window.pageYOffset || document.documentElement.scrollTop;
		const menuOffset = offset(this.headerElement.current).top + stickyOffset;
		const menuOffsetWindowTop = top > menuOffset ? 0 : menuOffset - top;
		this.setState({
			menuPopupHeight: window.innerHeight - menuOffsetWindowTop - headerOffset,
		});

		if (this.props.properties.openCart) this.props.openCart(false);
		this.props.openMenu();
	}

	render() {
		const { properties, navigation, setup, header, pageType, checkout, location } = this.props;
		const { grid } = this.state;
		const { items = [], totals = false } = checkout.selection;
		if (properties.siteInitiated === false || properties.siteClosed === true) return null;

		const headerClasses = [s.header, (properties.openCart ? 'is-showingCart' : null)];
		const acceptedLanguages = ['en', 'fr-ca'];
		const currentLanguages = Object.keys(setup.languages).reduce((acc, l) => (acceptedLanguages.indexOf(l) !== -1 ? { ...acc, [l]: { ...setup.languages[l] } } : acc), {});

		const countries = Object.keys(setup.countries).reduce((acc, country) => (country !== '0' ? [...acc, { key: country, value: `${setup.countries[country].name}${(!setup.countries[country].shipTo ? ' *' : '')}`, disabled: !setup.countries[country].shipTo }] : [...acc]), []);
		const languages = Object.keys(currentLanguages).reduce((acc, l) => [...acc, { key: l, value: currentLanguages[l].name }], []);
		const onCountryChange = (id, value) => {
			this.props.changeCountry(value);
			this.props.changeAddressInput('address', 'country', value);
			this.props.changeAddressInput('shippingAddress', 'country', value);
			this.setState({
				location: false,
			});
		};

		return (
			<>
				<Notification type="campaign" content={header.campaign} setup={setup} notificationRemoved={this.notificationRemovedHandler} removeNotification={this.props.removeNotification} />
				<Notification type="cookies" content={header.cookies} setup={setup} notificationRemoved={this.notificationRemovedHandler} removeNotification={this.props.removeNotification} />
				<Notification type="popup" content={header.popup} setup={setup} notificationRemoved={this.notificationRemovedHandler} removeNotification={this.props.removeNotification} />
				{ setup.unableToSendToCountry ? <Notification type="error" content={[{ name: 'unableToSendToCountry', text: setup.statics.unableToSendToCountry }]} setup={setup} /> : null }
				<header className={headerClasses.join(' ')} ref={this.headerElement}>
					<div className={s.header_fixedContainer}>
						<Navigation navigation={navigation} location={location} pageType={pageType} setup={setup} menuPopupHeight={this.state.menuPopupHeight} openMenuHandler={this.openMobileMenuHandler} />
						<div className={s.header_links} ref={this.linksElement}>
							<Link className={s.header_logo} to={prefix(setup)}>
								<svg xmlns="http://www.w3.org/2000/svg" width="159" height="25" viewBox="0 0 159 25">
									<path fill="#000" d="M404.756693,40.772 L401.505229,40.772 C400.829755,40.772 400.315157,41.094 399.928458,41.643 L393.392555,50.825 C393.199706,51.083 393.102781,51.243 392.973882,51.243 C392.845982,51.243 392.781032,51.083 392.684108,50.825 L389.368694,41.643 C389.175844,41.127 388.789146,40.772 388.113672,40.772 L385.310858,40.772 C384.635385,40.772 384.088811,41.191 383.959912,41.868 L380,64.259 L380,64.357 C380,64.613 380.257799,64.71 380.482624,64.71 L384.732309,64.71 C385.022083,64.71 385.310858,64.421 385.375808,64.065 L387.725975,50.63 C387.75795,50.404 387.854874,50.372 387.919824,50.372 C388.015749,50.372 388.080698,50.437 388.145647,50.597 L390.109116,55.913 C390.27099,56.299 390.560764,56.46 390.978438,56.46 L393.55343,56.46 C393.972104,56.46 394.325828,56.299 394.616601,55.913 L398.446614,50.597 C398.575513,50.437 398.640463,50.372 398.737387,50.372 C398.800338,50.372 398.865287,50.404 398.865287,50.533 L398.865287,50.63 L396.51512,64.065 L396.51512,64.193 C396.51512,64.485 396.643021,64.71 396.900819,64.71 L401.150505,64.71 C401.408304,64.71 401.698078,64.613 401.763028,64.259 L405.722939,41.9 C405.722939,41.803 405.754914,41.738 405.754914,41.674 C405.754914,41.127 405.369215,40.772 404.756693,40.772 Z M422.598778,46.797 C420.50641,46.314 418.124268,45.991 415.935974,45.991 C410.880917,45.991 407.404628,48.375 406.439381,53.981 C406.342456,54.594 405.923783,57.01 405.923783,57.01 C405.827858,57.622 405.761909,58.202 405.761909,58.814 C405.761909,62.036 407.307703,64.999 411.17169,64.999 C412.87736,64.999 414.422155,64.485 416.160799,63.131 L416.0319,64.066 L416.0319,64.163 C416.0319,64.452 416.257724,64.71 416.580472,64.71 L419.862912,64.71 C420.217635,64.71 420.538385,64.42 420.602335,64.066 L423.436125,47.764 C423.4681,47.667 423.4681,47.57 423.4681,47.506 C423.4681,47.023 423.146351,46.927 422.598778,46.797 Z M416.160799,59.523 C415.098627,60.134 413.843606,60.65 412.74846,60.65 C411.29959,60.65 410.913891,59.748 410.913891,58.685 C410.913891,58.33 410.946865,57.977 411.010815,57.623 L411.590363,54.239 C412.042011,51.631 412.975283,50.278 415.260501,50.278 C416.161798,50.278 417.16002,50.374 417.739568,50.47 L416.160799,59.523 Z M436.673802,49.696 L437.220375,46.636 L437.220375,46.539 C437.220375,46.216 436.995551,45.992 436.673802,45.992 C434.967133,45.992 433.304429,46.218 431.728658,47.539 L431.856558,46.895 L431.856558,46.765 C431.856558,46.475 431.631734,46.25 431.309985,46.25 L427.478972,46.25 C427.125248,46.25 426.803499,46.54 426.73855,46.895 L423.712909,64.034 L423.712909,64.163 C423.712909,64.452 423.937734,64.712 424.259483,64.712 L428.155445,64.712 C428.509169,64.712 428.863892,64.389 428.927842,64.034 L431.14911,51.437 C432.437106,50.793 434.260683,50.309 435.837454,50.309 L436.095253,50.309 C436.384028,50.308 436.609851,49.986 436.673802,49.696 Z M451.282409,46.733 C449.705638,46.315 447.93402,45.991 445.680777,45.991 C441.816791,45.991 438.146653,47.28 438.146653,51.79 C438.146653,53.207 438.79115,54.69 440.24002,55.625 L444.007083,58.041 C444.714531,58.491 444.97233,58.91 444.97233,59.329 C444.97233,60.682 443.942133,60.779 442.847986,60.779 C441.432091,60.779 439.144874,60.555 437.439205,60.327 L437.34228,60.327 C436.956581,60.327 436.666807,60.555 436.601858,60.94 L436.118235,63.55 L436.118235,63.712 C436.118235,64.002 436.280109,64.195 436.601858,64.291 C438.179627,64.743 440.561769,65 442.332389,65 C446.48515,65 450.284187,63.358 450.284187,58.686 C450.284187,57.43 449.737613,55.979 447.74117,54.691 L444.167957,52.403 C443.653358,52.082 443.395559,51.791 443.395559,51.404 C443.395559,50.31 444.33083,50.278 445.166179,50.278 L445.294079,50.278 C446.936798,50.278 449.672664,50.568 450.573961,50.696 L450.669886,50.696 C451.023611,50.696 451.249434,50.439 451.313385,50.084 L451.765032,47.475 L451.765032,47.313 C451.765032,47.023 451.604158,46.83 451.282409,46.733 Z M463.243081,45.992 C462.597584,45.992 461.536412,46.089 460.571165,46.219 L461.569386,40.644 L461.569386,40.547 C461.568387,40.225 461.343562,40 461.020814,40 L457.124852,40 C456.771128,40 456.417404,40.29 456.353454,40.644 L452.361567,63.228 C452.329592,63.325 452.329592,63.423 452.329592,63.486 C452.329592,63.808 452.522442,64.002 453.198914,64.163 C454.969533,64.614 457.415626,65 459.893693,65 C465.20555,65 468.489989,62.487 469.455236,56.752 L469.87391,54.239 C469.969835,53.628 470.034784,53.016 470.034784,52.436 C470.033785,48.603 467.717591,45.992 463.243081,45.992 Z M464.657977,54.239 L464.239304,56.752 C463.82163,59.362 462.952308,60.714 460.66709,60.714 C459.604918,60.714 458.638672,60.619 458.059124,60.522 L459.828744,50.47 C460.634116,50.373 461.727263,50.309 462.501659,50.309 C464.239304,50.309 464.819851,51.179 464.819851,52.661 C464.819851,53.144 464.754902,53.66 464.657977,54.239 Z M480.224837,40.516 C480.224837,40.225 480.030988,40 479.709239,40 L475.781303,40 C475.427578,40 475.073854,40.29 475.008905,40.644 L470.889118,64.035 L470.889118,64.131 C470.889118,64.452 471.112943,64.712 471.435692,64.712 L475.362629,64.712 C475.717353,64.712 476.040101,64.389 476.103052,64.035 L480.224837,40.644 L480.224837,40.516 L480.224837,40.516 Z M496.809903,46.797 C494.717535,46.314 492.334393,45.991 490.1461,45.991 C485.092041,45.991 481.615752,48.375 480.650505,53.981 C480.552582,54.594 480.133908,57.01 480.133908,57.01 C480.036984,57.622 479.972034,58.202 479.972034,58.814 C479.972034,62.036 481.518828,64.999 485.380816,64.999 C487.087485,64.999 488.63328,64.485 490.371924,63.131 L490.243024,64.066 L490.243024,64.163 C490.243024,64.452 490.469847,64.71 490.789598,64.71 L494.074037,64.71 C494.427761,64.71 494.748511,64.42 494.814459,64.066 L497.64725,47.764 C497.679225,47.667 497.679225,47.57 497.679225,47.506 C497.679225,47.023 497.356477,46.927 496.809903,46.797 Z M490.371924,59.523 C489.310751,60.134 488.054731,60.65 486.959585,60.65 C485.510715,60.65 485.126015,59.748 485.126015,58.685 C485.126015,58.33 485.156991,57.977 485.220941,57.623 L485.800489,54.239 C486.251137,51.631 487.185409,50.278 489.470627,50.278 C490.371924,50.278 491.370145,50.374 491.948694,50.47 L490.371924,59.523 Z M517.652648,40.547 C517.652648,40.225 517.394849,40 517.072101,40 L513.208114,40 C512.85539,40 512.53364,40.29 512.468691,40.644 L511.46947,46.219 C510.568173,46.089 509.505002,45.992 508.862503,45.992 C503.259873,45.992 500.330158,48.312 499.331936,54.239 L498.914262,56.752 C498.817337,57.333 498.785362,57.88 498.785362,58.428 C498.785362,62.262 501.200479,65 505.513116,65 C507.993181,65 510.568173,64.614 512.500666,64.163 C513.400964,63.97 513.561839,63.744 513.658763,63.228 L517.651649,40.644 L517.652648,40.547 L517.652648,40.547 Z M508.960427,60.522 C508.31593,60.619 507.350682,60.714 506.288511,60.714 C504.518891,60.714 503.938344,59.941 503.938344,58.459 C503.938344,57.977 504.004292,57.397 504.099218,56.752 L504.518891,54.239 C504.967541,51.598 505.773912,50.31 508.124079,50.31 C508.895478,50.31 509.991623,50.373 510.732045,50.47 L508.960427,60.522 Z M527.54893,45.992 C522.268049,45.992 518.888684,48.859 517.923437,54.465 L517.536739,56.72 C517.440813,57.366 517.376863,57.977 517.376863,58.525 C517.376863,63.004 520.660303,65 524.137591,65 C526.936408,65 528.741001,64.743 530.89632,64.227 C531.477866,64.099 531.606766,63.873 531.700693,63.358 L532.15334,60.909 L532.15334,60.813 C532.15334,60.522 531.926516,60.395 531.636742,60.395 L531.540817,60.395 C530.221846,60.522 527.324106,60.684 525.295688,60.684 C523.234295,60.684 522.430922,60.104 522.430922,58.43 C522.430922,58.043 522.493873,57.592 522.559821,57.108 L532.892763,57.108 C533.312436,57.108 533.603209,56.819 533.699134,56.335 L534.022882,54.466 C534.119806,53.887 534.182757,53.275 534.182757,52.726 C534.179759,48.956 531.897539,45.992 527.54893,45.992 Z M529.158675,53.886 L523.105396,53.886 C523.589019,51.662 524.619215,50.181 526.808508,50.181 C528.611102,50.181 529.318562,50.986 529.318562,52.467 C529.31955,52.887 529.255599,53.37 529.158675,53.886 Z M537.220388,45.712 C537.434221,45.628 537.596095,45.438 537.596095,45.129 L537.596095,45.09 C537.596095,44.574 537.214393,44.407 536.719778,44.407 C536.510941,44.407 536.263134,44.425 536.12824,44.445 C536.03831,44.461 535.996342,44.487 535.996342,44.586 L535.996342,46.543 C535.996342,46.575 536.022322,46.601 536.056296,46.601 L536.435,46.601 C536.465976,46.601 536.493954,46.575 536.493954,46.543 L536.493954,45.802 C536.549911,45.802 536.639841,45.808 536.701792,45.808 L536.731769,45.805 L537.123463,46.543 C537.138452,46.572 537.149443,46.601 537.183417,46.601 L537.62807,46.601 C537.660045,46.601 537.671036,46.586 537.671036,46.566 C537.671036,46.557 537.669038,46.545 537.663043,46.534 L537.220388,45.712 Z M537.10248,45.128 C537.10248,45.326 537.005555,45.405 536.701792,45.405 C536.669817,45.405 536.52493,45.402 536.493954,45.398 L536.493954,44.838 C536.526929,44.835 536.681808,44.83 536.721777,44.83 C536.966586,44.83 537.105477,44.912 537.105477,45.089 L537.105477,45.128 L537.10248,45.128 L537.10248,45.128 Z M536.72977,43.278 C535.478746,43.278 534.46054,44.297 534.46054,45.551 C534.46054,46.803 535.480745,47.822 536.72977,47.822 C537.982793,47.822 539,46.801 539,45.551 C539,44.297 537.980795,43.278 536.72977,43.278 Z M536.72977,47.411 C535.706568,47.411 534.874218,46.575 534.874218,45.551 C534.874218,44.525 535.706568,43.691 536.72977,43.691 C537.752973,43.691 538.587322,44.525 538.587322,45.551 C538.587322,46.575 537.752973,47.411 536.72977,47.411 Z" transform="translate(-380 -40)" />
								</svg>
							</Link>
							<div className={s.functions}>
								<div className={[s.functions_location, ...(this.state.location ? [s.functions_location__open] : [])].join(' ')} ref={this.languageCountryElement}>
									<a className={s.functions_location_title} href="#openLocation" onClick={this.openLocation}>
										<span>{ setup.countries[setup.country].currency }</span> <span>({ setup.country })</span>{ (setup.language === 'fr-ca' || setup.country === 'CA') && (<span className={s.functions_location_title_language}>{ setup.language }</span>) }
									</a>
									<div className={s.functions_location_dropdown}>
										<div className={s.functions_location_dropdown_inner}>
											{
												(setup.language !== 'en' || setup.country === 'CA') && (
													<div className={s.functions_location_dropdown_country}>
														<Select id="language" selected={setup.language} options={languages} inputChange={this.changeLanguage} />
													</div>
												)
											}
											<div className={s.functions_location_dropdown_country}>
												<Select id="country" selected={setup.country} options={countries} inputChange={onCountryChange} />
											</div>
											{ setup.unableToSendToCountry ? <span className={s.functions_location_dropdown_shipTo}>* {translate(setup, setup.statics.notShippingTo.language).text}</span> : null }
										</div>
									</div>
								</div>
								<Link className={[s.functions_selection, (items.length > 0 ? s.functions_selection__items : null)].join(' ')} to={prefix(setup, 'selection')} data-amount={totals && totals.totalQuantity} onClick={this.openCartHandler}>
									<svg xmlns="http://www.w3.org/2000/svg" width="25" height="21" viewBox="0 0 25 21">
										<path fill="#000" d="M8.6258 15.9507C8.1918 15.9507 7.8208 15.6617 7.7458 15.2647L4.8918 3.4697 1.1308 2.1837C.6678 2.0247.4308 1.5477.6018 1.1177.7718.6887 1.2858.4687 1.7488.6267L5.9918 2.0777C6.2898 2.1797 6.5078 2.4217 6.5628 2.7127L6.8648 4.3087 24.1078 3.3207C24.4008 3.3217 24.6758 3.4557 24.8428 3.6797 25.0088 3.9047 25.0458 4.1907 24.9418 4.4457L22.6798 15.4157C22.5478 15.7377 22.2158 15.9507 21.8448 15.9507L8.6258 15.9507zM9.3758 14.2917L21.2288 14.2917 22.8118 4.9787 7.1788 5.9677 9.3758 14.2917zM11.6725 18.8948C11.6725 19.8178 10.8665 20.5668 9.8725 20.5668 8.8785 20.5668 8.0725 19.8178 8.0725 18.8948 8.0725 17.9718 8.8785 17.2228 9.8725 17.2228 10.8665 17.2228 11.6725 17.9718 11.6725 18.8948M21.1531 18.7834C21.1531 19.7064 20.3471 20.4544 19.3531 20.4544 18.3591 20.4544 17.5531 19.7064 17.5531 18.7834 17.5531 17.8604 18.3591 17.1114 19.3531 17.1114 20.3471 17.1114 21.1531 17.8604 21.1531 18.7834" />
									</svg>
								</Link>
								<div className={s.cart} ref={this.cartElement} style={{ height: this.state.menuPopupHeight === 'auto' ? 'auto' : `${this.state.menuPopupHeight + headerOffset}px` }}>
									<Cart closeCartHandler={this.closeCartHandler} />
								</div>
								<button className={s.cartScreen} onClick={this.closeCartHandler} type="button" aria-label="Close cart" />
							</div>
						</div>
					</div>
				</header>
				{ grid ? <div className={s.grid}><div /></div> : null }
			</>
		);
	}
}

const mapState = ({ environment, checkout, location }) => {
	const { properties, navigation, setup, header, pagePaths, pageType } = environment;
	return { properties, navigation, setup, header, pagePaths, pageType, checkout, location };
};

export default hot(module)(connect(mapState, {
	removeNotification,
	openCart,
	openMenu,
	clearFilter,
	resetFilter,
	changeCountry,
	changeAddressInput,
	changeLanguage,
	goToPage,
	clientInit,
	changeDevice,
})(Header));
